// Sender ↔ contact connection guardrail.
// Queries v_sender_contact_connections (backed by heyreach_connections, refreshed
// every 6h from heyreach_campaign_leads) to confirm a sender is actually connected
// to a contact on LinkedIn before allowing followup-message generation.
//
// Use this anywhere a `linkedin_connection_followup` message is being authored.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ConnectionCheck =
  | { status: 'connected'; connectedAt: string | null; source: string | null }
  | { status: 'not_connected' }
  | { status: 'unknown' };

function slugFromUrl(url: string | null | undefined): string {
  if (!url) return '';
  const m = url.match(/\/in\/([^/?]+)/i);
  return m ? m[1].toLowerCase() : '';
}

/**
 * Returns whether the given sender (by HeyReach account ID) is recorded in our
 * local connection cache as connected to the given LinkedIn URL.
 *
 * `'unknown'` = inputs missing → caller should not block on this.
 */
export function useSenderConnection(
  senderHeyreachAccountId: string | null | undefined,
  contactLinkedinUrl: string | null | undefined,
  enabled: boolean = true,
) {
  const slug = slugFromUrl(contactLinkedinUrl);

  return useQuery<ConnectionCheck>({
    queryKey: ['sender-connection', senderHeyreachAccountId, slug],
    enabled: enabled && !!senderHeyreachAccountId && !!slug,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!senderHeyreachAccountId || !slug) return { status: 'unknown' };
      const { data, error } = await supabase
        .from('v_sender_contact_connections' as any)
        .select('connected_at, connection_source')
        .eq('sender_heyreach_id', senderHeyreachAccountId)
        .eq('linkedin_slug', slug)
        .order('connected_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('useSenderConnection query error:', error);
        return { status: 'unknown' };
      }
      if (!data) return { status: 'not_connected' };
      const row = data as unknown as { connected_at: string | null; connection_source: string | null };
      return { status: 'connected', connectedAt: row.connected_at, source: row.connection_source };
    },
  });
}
